// LOCAL: epoch table from sui_getCheckpoints (no suix_getEpochMetrics).

import { ArrowRight12 } from '@mysten/icons';
import { Text } from '@mysten/ui';
import { useMemo, useState } from 'react';

import { genTableDataFromCpcEpochsData } from './cpcEpochUtils';
import { useCpcEpochSummaries } from '~/hooks/useCpcEpochSummaries';
import { Link } from '~/ui/Link';
import { PlaceholderTable } from '~/ui/PlaceholderTable';
import { TableCard } from '~/ui/TableCard';
import { numberSuffix } from '~/utils/numberUtil';

const DEFAULT_EPOCHS_LIMIT = 20;

interface Props {
	disablePagination?: boolean;
	refetchInterval?: number;
	initialLimit?: number;
}

export function CpcEpochsActivityTable({
	disablePagination,
	refetchInterval,
	initialLimit = DEFAULT_EPOCHS_LIMIT,
}: Props) {
	const [limit, setLimit] = useState(initialLimit);
	const { data, isPending, isFetching, isError } = useCpcEpochSummaries(limit, refetchInterval);

	const cardData = useMemo(
		() => (data?.epochs.length ? genTableDataFromCpcEpochsData(data.epochs) : undefined),
		[data?.epochs],
	);

	const count = data?.currentEpoch;

	return (
		<div className="flex flex-col space-y-3 text-left xl:pr-10 p-6 pb-10">
			{isError && (
				<div className="pt-2 font-sans font-semibold text-issue-dark">Failed to load Epochs</div>
			)}
			{isPending || isFetching || !cardData ? (
				<PlaceholderTable
					rowCount={limit}
					rowHeight="16px"
					colHeadings={[
						'Epoch',
						'Transaction Blocks',
						'Stake Rewards',
						'Checkpoint Set',
						'Storage Net Inflow',
						'Epoch End',
					]}
					colWidths={['100px', '120px', '40px', '204px', '90px', '38px']}
				/>
			) : (
				<div>
					<TableCard data={cardData.data} columns={cardData.columns} />
				</div>
			)}

			<div
				className="flex justify-between bg-white/10 p-6"
				style={{ bottom: '0px', position: 'absolute', left: '0px', width: '100%' }}
			>
				{!disablePagination ? (
					<Text variant="body/medium" color="steel">
						Checkpoint-derived epochs (CPC)
					</Text>
				) : (
					<Link
						to="/recent?tab=epochs"
						after={<ArrowRight12 className="h-3 w-3 -rotate-45" />}
						className="text-[#aeb9d0]"
					>
						View all
					</Link>
				)}

				<div className="flex items-center space-x-3">
					<Text variant="body/medium" color="dark">
						{count ? numberSuffix(Number(count)) : '-'}
						{` Total`}
					</Text>
					{!disablePagination && (
						<select
							className="form-select rounded-md border border-gray-45 px-3 py-2 pr-8 text-bodySmall font-medium leading-[1.2] text-steel-dark shadow-button"
							value={limit}
							onChange={(e) => setLimit(Number(e.target.value))}
						>
							<option value={20}>20 Per Page</option>
							<option value={40}>40 Per Page</option>
							<option value={60}>60 Per Page</option>
						</select>
					)}
				</div>
			</div>
		</div>
	);
}
